import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { Brain } from 'lucide-react';
import TitleScreen from './components/TitleScreen';
import GameScreen from './components/GameScreen';
import ResultScreen from './components/ResultScreen';
import RankingScreen from './components/RankingScreen';
import AdminLoginScreen from './components/AdminLoginScreen';
import PvPGameScreen from './components/PvPGameScreen';
import { useUser } from './hooks/useUser';
import { useGame } from './hooks/useGame';

const AdminScreen = React.lazy(() => import('./components/AdminScreen'));

// --- メインコンポーネント ---
export default function App() {
  const { userId, userName, highScores, updateUserName, fetchHighScores } = useUser();
  const {
    gameState, topicChar, score, history, rejectedWords, applicationCandidates,
    startGame, resetGame, showRanking, finishGame, setHistory, setGameState
  } = useGame(userId, fetchHighScores);

  const [adminToken, setAdminToken] = useState(() => {
    try { return sessionStorage.getItem('kb_admin_token'); } catch (e) { return null; }
  });

  // Routing
  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === '#/admin') {
        setGameState(adminToken ? 'admin' : 'admin_login');
      }
    };

    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, [setGameState, adminToken]);

  const handleAdminBack = useCallback(() => {
    try { sessionStorage.removeItem('kb_admin_token'); } catch (e) {}
    setAdminToken(null);
    window.location.hash = ''; // Clear hash
    resetGame();
  }, [resetGame]);

  const handleAdminSessionExpired = useCallback(() => {
    try { sessionStorage.removeItem('kb_admin_token'); } catch (e) {}
    setAdminToken(null);
    setGameState('admin_login');
  }, [setGameState]);

  const startPvP = () => {
    setGameState('pvp_playing');
  };

  return (
    <div className="min-h-screen bg-orange-50 text-gray-800 font-sans flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-orange-100">

        {/* ヘッダー */}
        <div className="bg-orange-600 p-4 text-white text-center shadow-md z-10 relative">
          <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
            <Brain className="w-8 h-8" />
            ことバトル
          </h1>
          <p className="text-orange-100 text-xs mt-1">漢字・カタカナ対応版</p>
        </div>

        {/* コンテンツ */}
        <div className="p-6 bg-white min-h-[400px]">
          {gameState === 'title' && (
            <TitleScreen
              onStart={startGame}
              highScores={highScores}
              onShowRanking={showRanking}
              userName={userName}
              onUpdateName={updateUserName}
              onStartPvP={startPvP}
            />

          )}

          {gameState === 'playing' && (
            <GameScreen
              topic={topicChar}
              onFinish={finishGame}
              history={history}
              setHistory={setHistory}
            />
          )}

          {gameState === 'result' && (
            <ResultScreen
              score={score}
              history={history}
              rejectedWords={rejectedWords}
              onRetry={resetGame}
              highScores={highScores}
              onShowRanking={showRanking}
              applicationCandidates={applicationCandidates}
              userId={userId}
            />
          )}

          {gameState === 'ranking' && (
            <RankingScreen onBack={resetGame} />
          )}

          {gameState === 'admin_login' && (
            <AdminLoginScreen onLoginSuccess={(t) => {
              try { sessionStorage.setItem('kb_admin_token', t); } catch (e) {}
              setAdminToken(t);
              setGameState('admin');
            }} />
          )}

          {gameState === 'admin' && (
            <Suspense fallback={<div className="text-center text-gray-400 py-10">読み込み中...</div>}>
              <AdminScreen
                onBack={handleAdminBack}
                token={adminToken}
                onSessionExpired={handleAdminSessionExpired}
              />
            </Suspense>
          )}

          {gameState === 'pvp_playing' && (
            <PvPGameScreen userId={userId} onBack={resetGame} />
          )}
        </div>

        {/* フッター */}
        <div className="bg-gray-50 p-3 text-center text-[10px] text-gray-400 border-t">
          Prototype v0.3 - monitoring
        </div>
      </div>
    </div>
  );
}